import { privateApi } from '@/config';
import {
  Notification,
  NotificationFilters,
  NotificationResponse,
  NotificationStats,
  SendToUserPayload,
} from '../types/notification.types';
import { normalizarStats } from './notificacion-del-servidor';

/**
 * GH#2666 — el 404 que SÍ es «la notificación no existe» (la ruta existe): lo dice el propio API con
 * `Notification with ID <id> not found`. Un 404 «Cannot PATCH …» es OTRA cosa (la RUTA no existe: el
 * defecto original de GH#2650) y no se tolera.
 */
const esNotificacionInexistente = (err: any): boolean =>
  err?.response?.status === 404 &&
  /^Notification with ID .+ not found$/.test(String(err.response?.data?.message ?? ''));

export const notificationsApi = {
  /**
   * Obtener notificaciones del usuario
   */
  async getNotifications(filters?: Partial<NotificationFilters>): Promise<NotificationResponse> {
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.entityType) params.append('entityType', filters.entityType);
    // Bug #37 fix (audit Nivel 2 2026-06-03): NO enviar userId/tenantId vía
    // query params. El backend los extrae del JWT (SEC-A7 TenantScopeInterceptor).
    // Enviarlos como query daba 400 IsUUID strict rechazo + era anti-pattern
    // security (cross-tenant attempt potencial).
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await privateApi.get(`/notifications?${params.toString()}`);
    return response.data;
  },

  /**
   * Obtener estadísticas de notificaciones
   */
  async getStats(): Promise<NotificationStats> {
    const response = await privateApi.get('/notifications/stats');
    // GH#2679 — el cuerpo viene envuelto (`{ success, data: { total, unread, byType } }`): sin desenvolver, `stats.unread` era undefined.
    return normalizarStats(response.data);
  },

  /**
   * Marcar notificaciones como leídas
   */
  async markAsRead(notificationIds: string[]): Promise<void> {
    // El backend solo permite marcar una notificación a la vez: PATCH /notifications/:id/read
    // (apps/api/src/core/notifications/controllers/notifications.controller.ts).
    //
    // GH#2650 — esto era PUT, que el API no tiene («Cannot PUT»), y el 404 se tragaba aquí
    // mismo: marcar como leída nunca llegaba al servidor y la notificación volvía a salir sin
    // leer al recargar. Se tolera SOLO el 404 de «la notificación no existe» (las de prueba,
    // metadata.test, solo existen en el cliente; GH#2666). Cualquier otro fallo —500, 400, red, un
    // 404 de ruta inexistente— no se traga: se propaga.
    const resultados = await Promise.allSettled(
      notificationIds.map(id =>
        privateApi.patch(`/notifications/${id}/read`).catch(err => {
          if (esNotificacionInexistente(err)) {
            return;
          }
          throw err;
        })
      )
    );
    const fallo = resultados.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (fallo) {
      throw fallo.reason;
    }
  },

  /**
   * Marcar notificaciones como no leídas
   */
  async markAsUnread(notificationIds: string[]): Promise<void> {
    // El backend no tiene endpoint para marcar como no leído
    // Simular la operación (en una implementación real, esto requeriría un endpoint backend)
    // En el futuro, esto sería: await privateApi.put('/notifications/mark-unread', { notificationIds });
  },

  /**
   * Marcar todas las notificaciones como leídas
   */
  async markAllAsRead(): Promise<void> {
    // GH#2650 — la ruta del API se llama `read-all`, no `mark-all-read`. Ojo: mientras
    // `@Patch(':id')` siga declarado ANTES de `@Patch('read-all')` en el controller, `:id` la
    // atrapa y da 400 (medido en producción); el arreglo de orden es de BACKEND.
    await privateApi.patch('/notifications/read-all');
  },

  /**
   * Eliminar una notificación
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await privateApi.delete(`/notifications/${notificationId}`);
  },

  /**
   * Eliminar todas las notificaciones leídas
   */
  async deleteReadNotifications(): Promise<void> {
    await privateApi.delete('/notifications/read');
  },

  /**
   * Obtener una notificación específica
   */
  async getNotification(notificationId: string): Promise<Notification> {
    const response = await privateApi.get(`/notifications/${notificationId}`);
    return response.data;
  },

  /**
   * Actualizar configuración de notificaciones del usuario
   */
  async updateSettings(settings: {
    emailNotifications?: boolean;
    pushNotifications?: boolean;
    smsNotifications?: boolean;
    notificationTypes?: string[];
  }): Promise<void> {
    await privateApi.patch('/notifications/settings', settings);
  },

  /**
   * Obtener configuración de notificaciones del usuario
   */
  async getSettings(): Promise<{
    emailNotifications: boolean;
    pushNotifications: boolean;
    smsNotifications: boolean;
    notificationTypes: string[];
  }> {
    const response = await privateApi.get('/notifications/settings');
    return response.data;
  },

  /**
   * GH#2673 — enviar una notificación a OTRA persona. Se migró aquí desde `shared/services/notification.service.ts` (segundo cliente,
   * borrado) para que haya UN solo cliente bajo la guarda de contrato.
   * El API la expone desde GH#2673 (`notifications.controller.ts`, `POST broadcast/user`): el destinatario tiene que ser un usuario
   * del MISMO tenant del token (de otro tenant o inexistente: 404) y `actionUrl` sólo acepta una ruta relativa del panel.
   * HOY NO TIENE LLAMADOR VIVO: el único (el aviso del embudo de clientes, en `AgencyClientsTemplate`) era una plantilla que ninguna ruta
   * montaba y se retiró; la ruta se queda, restringida a administradores del tenant mientras no haya consumidor (decisión de la CTO, GH#2706).
   */
  async sendToUser(data: SendToUserPayload): Promise<void> {
    await privateApi.post('/notifications/broadcast/user', data);
  },
};

export default notificationsApi;
